import MailFolderNode from './MailFolderNode';
import { observer } from 'mobx-react';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import Droppable from 'owa-dnd/lib/components/Droppable';
import createDropViewState from 'owa-dnd/lib/utils/createDropViewState';
import { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { FolderForestNodeType, FolderForestTreeType } from 'owa-favorites-types';
import loc, { format } from 'owa-localize';
import getFolderViewStateFromId from 'owa-mail-folder-store/lib/selectors/getFolderViewStateFromId';
import MailFolderDragData from 'owa-mail-folder-store/lib/store/schema/MailFolderDragData';
import { FolderTextFieldOperation } from 'owa-mail-folder-store/lib/store/schema/FolderTextFieldViewState';
import MailFolderNodeViewState from 'owa-mail-folder-store/lib/store/schema/MailFolderNodeViewState';
import { default as viewStateStore } from 'owa-mail-folder-store/lib/store/store';
import { MailFolderContextMenu } from 'owa-mail-folder-view';
import { ActionSource } from 'owa-mail-store';
import { getAnchorForContextMenu } from 'owa-positioning';
import { LazyPublicFolderPicker } from 'owa-publicfolder-picker';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import TreeNode from 'owa-tree-node/lib/components/TreeNode';
import { ChevronProps } from 'owa-tree-node/lib/components/TreeNode';
import * as React from 'react';
import {
    ARCHIVE_FOLDERS_TREE_TYPE,
    Folder,
    getEffectiveFolderDisplayName,
    shouldDisplayFolderInFolderTree,
    getFolderTable,
    MailboxLoadStateEnum,
    PRIMARY_FOLDERS_TREE_TYPE,
    SHARED_FOLDERS_TREE_TYPE,
} from 'owa-folders';
import { LazyFolderTextField } from 'owa-folders-common';
import {
    newFolderAction,
    newFolderAnnouncementText,
    renameFolderAnnouncementText,
} from './MailFolderList.locstring.json';
import {
    lazyMoveFolder,
    lazyNewFolderFromTextField,
    lazyRenameFolder,
    lazyToggleFolderTreeExpansion,
    lazyDismissMailFolderTextField,
} from 'owa-mail-folder-store';
import {
    hideFolderTreeContextMenu,
    showFolderTreeContextMenu,
} from 'owa-mail-folder-store/lib/actions/folderTreeContextMenu';
import { getDensityModeString } from 'owa-session-store';
import { lazyKeydown } from 'owa-hotkeys';
import { LazyAction, LazyModule } from 'owa-bundling';

const lazyModule = new LazyModule(() => import('./lazy/lazyFolderListHotKeys'));
const lazySetupMailFolderListKeys = new LazyAction(lazyModule, m => m.setupMailFolderListKeys);
const lazyOnNewFolderNodeClicked = new LazyAction(lazyModule, m => m.onNewFolderNodeClicked);
const styles = require('owa-tree-node/lib/components/NodeHeight.scss');
let classNames = require('classnames/bind').bind(styles);

export interface MailFolderListProps extends React.HTMLProps<HTMLDivElement> {
    childFolderIds: string[]; // Children folder ids to render the sub-folders under root
    isFavoritesSupported: boolean; // this tree's folders can or can't support favorites for folder
    isRootExpanded: boolean; // Whether the root node should be in an expanded state
    loadingState: MailboxLoadStateEnum; // Loading state of the folder tree
    onDeleteFolderCallback: (folder: Folder, actionSource: ActionSource) => void; // Callback on click of Delete folder
    onRootNodeChevronClickedCallback: () => void; // Callback on click of root folder
    rootDisplayName: string; // Display name of root node
    rootFolder: Folder; // Root folder details
    showRootNode: boolean; // Whether or not to show root node
    treeType: FolderForestTreeType; // Type of folder tree
    shouldBeDroppable: boolean;
}

/**
 * Folder list that renders a root node and all folders supplied to it and their children.
 */
@observer
@lazyKeydown(lazySetupMailFolderListKeys.importAndExecute)
export default class MailFolderList extends React.Component<MailFolderListProps, any> {
    constructor(props: MailFolderListProps) {
        super(props);
        this.state = {
            folderUpdateText: '',
        };
    }

    render() {
        const rootFolder = this.props.rootFolder;
        const contextMenuState = viewStateStore.contextMenuState;
        const shouldShowLoadingSpinner = this.props.loadingState === MailboxLoadStateEnum.Loading;

        const ariaProps: AriaProperties = {
            role: AriaRoles.tree,
        };

        if (!rootFolder) {
            return null;
        }
        this.forceExpandFolderTreeIfNecessary();

        return (
            <div
                style={this.props.style}
                className={this.props.className}
                {...generateDomPropertiesForAria(ariaProps)}>
                {this.props.showRootNode && this.renderFolderRoot()}
                {this.props.isRootExpanded && shouldShowLoadingSpinner && (
                    <Spinner size={SpinnerSize.medium} />
                )}
                {// Render all the nodes starting with the root node
                // ChildfolderIds would initially be null for Archive root until the root is clicked
                this.props.childFolderIds && this.renderChildren(this.props.childFolderIds, 0)}
                {this.shouldShowContextMenu() && (
                    <MailFolderContextMenu
                        anchorPoint={contextMenuState.anchor}
                        folder={contextMenuState.folder}
                        onDismiss={this.onContextMenuDismissed}
                        onDeleteFolder={this.props.onDeleteFolderCallback}
                        shouldHideToggleFavorite={!this.props.isFavoritesSupported}
                    />
                )}
                <LazyPublicFolderPicker />
                <span className="screenReaderOnly" aria-live="assertive" aria-atomic="true">
                    {this.state.folderUpdateText}
                </span>
            </div>
        );
    }

    /**
     * Returns whether the context menu should be displayed or not
     */
    private shouldShowContextMenu(): boolean {
        const { contextMenuState } = viewStateStore;
        // Render the context menu if it is set to be visible and for different folder tree, we render a new context menu
        if (
            !contextMenuState ||
            !contextMenuState.folder ||
            contextMenuState.treeType != this.props.treeType
        ) {
            return false;
        }
        // Additionally for shared folder trees, we need to check the folderId property because treeType is same for all shared folder roots
        if (contextMenuState.treeType === SHARED_FOLDERS_TREE_TYPE) {
            if (
                contextMenuState.folder.principalSMTPAddress !=
                this.props.rootFolder.principalSMTPAddress
            ) {
                return false;
            }
        }
        return true;
    }

    private forceExpandFolderTreeIfNecessary() {
        if (!this.props.showRootNode && !this.props.isRootExpanded) {
            // If the 'Folders' root node is hidden, and the root node is collapsed
            // we need to explicitly expand the root node for it to be accessible
            lazyToggleFolderTreeExpansion.importAndExecute();
        }
    }

    private shouldRenderNodeInEditMode(
        folder: Folder,
        operation: FolderTextFieldOperation
    ): boolean {
        const folderTextFieldViewState = viewStateStore.folderTextFieldViewState;
        return (
            folderTextFieldViewState &&
            folderTextFieldViewState.folderId == folder.FolderId.Id &&
            folderTextFieldViewState.operation === operation &&
            (folderTextFieldViewState.folderNodeType === PRIMARY_FOLDERS_TREE_TYPE ||
                folderTextFieldViewState.folderNodeType === ARCHIVE_FOLDERS_TREE_TYPE ||
                folderTextFieldViewState.folderNodeType === SHARED_FOLDERS_TREE_TYPE)
        );
    }

    private renderFolderTextField(
        nestDepth: number,
        onEntry: (value: string, folderId: string) => void,
        folderId?: string,
        defaultValue?: string
    ): JSX.Element {
        return (
            <LazyFolderTextField
                className={classNames(getDensityModeString(), styles.nodeHeight)}
                depth={nestDepth}
                defaultValue={defaultValue}
                key="folderTextField"
                onEntry={onEntry}
                onDismiss={this.onDismissFolderTextFieldEntry}
                folderId={folderId}
            />
        );
    }

    private renderNode(
        folder: Folder,
        viewState: MailFolderNodeViewState,
        nestDepth: number
    ): JSX.Element {
        const folderId = folder.FolderId.Id;
        return this.shouldRenderNodeInEditMode(folder, 'rename') ? (
            this.renderFolderTextField(
                nestDepth,
                this.onRenameFolderTextFieldEntry,
                folderId,
                getEffectiveFolderDisplayName(folder)
            )
        ) : (
            <MailFolderNode
                depth={nestDepth}
                folderId={folderId}
                isBeingDragged={viewState.drag.isBeingDragged}
                key={folderId}
                treeType={this.props.treeType}
                onContextMenu={this.onContextMenu}
                shouldHideToggleFavorite={!this.props.isFavoritesSupported}
            />
        );
    }

    private renderAddNewFolderNode = (): JSX.Element => {
        return (
            <TreeNode
                displayName={loc(newFolderAction)}
                isCustomActionNode={true}
                isRootNode={false}
                key={'newFolder'}
                onClick={this.onNewFolderNodeClicked}
            />
        );
    };

    private onNewFolderNodeClicked = (evt: React.MouseEvent<unknown> | React.KeyboardEvent) => {
        lazyOnNewFolderNodeClicked.importAndExecute(
            evt,
            this.props.rootFolder.FolderId.Id,
            this.props.treeType
        );
    };

    private onRootNodeContextMenu = (evt: React.MouseEvent<HTMLElement>) => {
        evt.preventDefault();
        if (this.shouldShowRootNodeContextMenu()) {
            showFolderTreeContextMenu(
                this.props.rootFolder.FolderId.Id,
                FolderForestNodeType.Folder,
                this.props.rootFolder,
                getAnchorForContextMenu(evt),
                this.props.treeType
            );
        }
    };
    /**
     *  We should show context menu on root nodes if tree type is shared (for removing shared user option)
     *  and primary tree root for public folders or add shared folder user or folder permissions options
     */
    private shouldShowRootNodeContextMenu() {
        return (
            this.props.treeType === SHARED_FOLDERS_TREE_TYPE ||
            (this.props.treeType === PRIMARY_FOLDERS_TREE_TYPE && !isConsumer())
        );
    }

    private onRootNodeChevronClicked = (evt: React.MouseEvent<unknown> | KeyboardEvent) => {
        evt.stopPropagation();
        this.props.onRootNodeChevronClickedCallback();
    };

    private onDrop = (dragData: DragData) => {
        const mailFolderItemBeingDragged = dragData as MailFolderDragData;
        const draggedFolderId = mailFolderItemBeingDragged.folderId;
        lazyMoveFolder.importAndExecute(
            this.props.rootFolder.FolderId.Id,
            draggedFolderId,
            this.props.treeType
        );
    };

    private canDrop = (dragData: DragData) => {
        const itemType = dragData.itemType;
        return (
            itemType === DraggableItemTypes.MailFolderNode &&
            this.props.shouldBeDroppable &&
            !!this.props.rootFolder
        );
    };

    private renderFolderRoot(): JSX.Element {
        const chevronProps: ChevronProps = {
            isExpanded: this.props.isRootExpanded,
            onClick: this.onRootNodeChevronClicked,
        };
        const emptyDropViewState = createDropViewState();

        return (
            <Droppable
                dropViewState={emptyDropViewState}
                onDrop={this.onDrop}
                canDrop={this.canDrop}>
                <TreeNode
                    chevronProps={chevronProps}
                    depth={0}
                    displayName={this.props.rootDisplayName}
                    isRootNode={true}
                    onClick={chevronProps.onClick}
                    onContextMenu={this.onRootNodeContextMenu}
                />
            </Droppable>
        );
    }

    private renderChildren(renderedFolderIds: string[], nestDepth: number): JSX.Element[] {
        const renderedNodes: JSX.Element[] = [];
        if (nestDepth == 0) {
            const rootFolder = this.props.rootFolder;
            if (this.props.isRootExpanded) {
                // Recursively add subfolders
                const renderedChildNodes = this.renderChildren(renderedFolderIds, nestDepth + 1);
                Array.prototype.push.apply(renderedNodes, renderedChildNodes);

                // Add new folder node or new folder text field at the end
                if (this.shouldRenderNodeInEditMode(rootFolder, 'new')) {
                    renderedNodes.push(
                        this.renderFolderTextField(nestDepth, this.onNewFolderTextFieldEntry)
                    );
                } else if (
                    this.props.treeType === PRIMARY_FOLDERS_TREE_TYPE ||
                    this.props.treeType === ARCHIVE_FOLDERS_TREE_TYPE
                ) {
                    renderedNodes.push(this.renderAddNewFolderNode());
                }
            }
        } else {
            renderedFolderIds.forEach(folderId => {
                const folder = getFolderTable().get(folderId);
                const viewState = getFolderViewStateFromId(folderId);
                if (shouldDisplayFolderInFolderTree(folder)) {
                    renderedNodes.push(this.renderNode(folder, viewState, nestDepth));

                    if (this.shouldRenderNodeInEditMode(folder, 'new')) {
                        renderedNodes.push(
                            this.renderFolderTextField(nestDepth, this.onNewFolderTextFieldEntry)
                        );
                    }

                    if (
                        viewState.isExpanded &&
                        folder.childFolderIds &&
                        folder.childFolderIds.length > 0
                    ) {
                        const renderedChildNodes = this.renderChildren(
                            folder.childFolderIds,
                            nestDepth + 1
                        );
                        Array.prototype.push.apply(renderedNodes, renderedChildNodes);
                    }
                }
            });
        }

        return renderedNodes;
    }

    private onNewFolderTextFieldEntry = (value: string) => {
        lazyNewFolderFromTextField.importAndExecute(value);
        this.setState({
            folderUpdateText: format(loc(newFolderAnnouncementText), value),
        });
    };

    private onRenameFolderTextFieldEntry = (value: string, folderId: string) => {
        lazyRenameFolder.importAndExecute(value, folderId);
        this.setState({
            folderUpdateText: format(loc(renameFolderAnnouncementText), value),
        });
    };

    private onDismissFolderTextFieldEntry() {
        lazyDismissMailFolderTextField.importAndExecute();
    }

    private onContextMenu = (evt: React.MouseEvent<HTMLElement>, folder: Folder) => {
        showFolderTreeContextMenu(
            folder.FolderId.Id,
            FolderForestNodeType.Folder,
            folder,
            getAnchorForContextMenu(evt),
            this.props.treeType
        );
    };

    private onContextMenuDismissed() {
        hideFolderTreeContextMenu();
    }
}
